import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export const INCIDENT_TYPES = [
  "misconduct",
  "safety",
  "grievance",
  "accident",
  "performance",
  "other",
] as const;
export const INCIDENT_SEVERITIES = ["low", "medium", "high", "critical"] as const;
export const INCIDENT_STATUSES = ["open", "investigating", "resolved", "closed"] as const;

export type IncidentType = (typeof INCIDENT_TYPES)[number];
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

/** A witness or party on an incident, as returned by the API. */
export type IncidentParticipant = {
  id: string;
  employeeId: string | null;
  name: string | null;
};

/** A participant as supplied on create/update (employee link OR free-text). */
export type IncidentParticipantInput = {
  employeeId?: string;
  name?: string;
};

export type Incident = {
  id: string;
  employeeId: string;
  type: IncidentType;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  description: string | null;
  location: string | null;
  witnesses: IncidentParticipant[];
  parties: IncidentParticipant[];
  incidentDate: string;
  reportedDate: string | null;
  followUpDate: string | null;
  resolution: string | null;
  recordedBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type IncidentInput = {
  employeeId: string;
  type: IncidentType;
  severity: IncidentSeverity;
  status: IncidentStatus;
  title: string;
  description?: string;
  location?: string;
  witnesses?: IncidentParticipantInput[];
  parties?: IncidentParticipantInput[];
  incidentDate: string;
  reportedDate?: string;
  followUpDate?: string;
  resolution?: string;
};

export type IncidentNote = {
  id: string;
  body: string;
  authorId: string | null;
  createdAt: string;
};

export type IncidentAttachment = {
  id: string;
  incidentId: string;
  fileName: string;
  url: string;
  contentType: string;
  sizeBytes: number | null;
  note: string | null;
  createdAt: string;
};

/** A document generated for / linked to an incident (summary shape). */
export type IncidentDocument = {
  id: string;
  kind: "builtin" | "custom";
  documentType: string;
  typeName: string;
  name: string;
  status: string;
  employeeId?: string;
  updatedAt: string;
};

/** An incident list item with the subject employee's name resolved server-side. */
export type IncidentListItem = Incident & { employeeName: string | null };

/** Filters + paging for the incident log (server-side). */
export type IncidentsPageParams = {
  limit: number;
  offset: number;
  q?: string;
  status?: IncidentStatus;
  severity?: IncidentSeverity;
  type?: IncidentType;
  employeeId?: string;
  sort?: "title" | "employee" | "type" | "severity" | "status" | "date";
  dir?: "asc" | "desc";
};

/** One page of incidents plus the overall total for the active filters. */
export type IncidentsPage = { items: IncidentListItem[]; total: number };

/** A filtered, paginated slice of the incident log (`/api/incidents/page`). */
export async function fetchIncidentsPage(params: IncidentsPageParams): Promise<IncidentsPage> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q) search.set("q", params.q);
  if (params.status) search.set("status", params.status);
  if (params.severity) search.set("severity", params.severity);
  if (params.type) search.set("type", params.type);
  if (params.employeeId) search.set("employeeId", params.employeeId);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  return request<IncidentsPage>(`/api/incidents/page?${search.toString()}`);
}

export async function listIncidents(employeeId?: string): Promise<Incident[]> {
  const q = employeeId ? `?employeeId=${encodeURIComponent(employeeId)}` : "";
  const data = await request<{ items: Incident[] }>(`/api/incidents${q}`);
  return data.items;
}

/** Incidents the employee is connected to as the subject OR a participant. */
export async function listIncidentsForEmployee(employeeId: string): Promise<Incident[]> {
  const data = await request<{ items: Incident[] }>(
    `/api/incidents?forEmployee=${encodeURIComponent(employeeId)}`,
  );
  return data.items;
}

export async function getIncident(id: string): Promise<Incident> {
  const data = await request<{ item: Incident }>(`/api/incidents/${id}`);
  return data.item;
}

export async function createIncident(input: IncidentInput): Promise<Incident> {
  const data = await request<{ item: Incident }>("/api/incidents", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return data.item;
}

export async function updateIncident(
  id: string,
  patch: Partial<Omit<IncidentInput, "employeeId">>,
): Promise<Incident> {
  const data = await request<{ item: Incident }>(`/api/incidents/${id}`, {
    method: "PUT",
    body: JSON.stringify(patch),
  });
  return data.item;
}

export async function deleteIncident(id: string): Promise<void> {
  await request<void>(`/api/incidents/${id}`, { method: "DELETE" });
}

// ── Notes ────────────────────────────────────────────────────────────────

export async function listIncidentNotes(incidentId: string): Promise<IncidentNote[]> {
  const data = await request<{ items: IncidentNote[] }>(`/api/incidents/${incidentId}/notes`);
  return data.items;
}

export async function createIncidentNote(incidentId: string, body: string): Promise<IncidentNote> {
  const data = await request<{ item: IncidentNote }>(`/api/incidents/${incidentId}/notes`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });
  return data.item;
}

export async function deleteIncidentNote(incidentId: string, noteId: string): Promise<void> {
  await request<void>(`/api/incidents/${incidentId}/notes/${noteId}`, {
    method: "DELETE",
  });
}

// ── Attachments ──────────────────────────────────────────────────────────

export async function listIncidentAttachments(incidentId: string): Promise<IncidentAttachment[]> {
  const data = await request<{ items: IncidentAttachment[] }>(
    `/api/incidents/${incidentId}/attachments`,
  );
  return data.items.map(normalizeIncidentAttachmentUrl);
}

function normalizeIncidentAttachmentUrl(att: IncidentAttachment): IncidentAttachment {
  return {
    ...att,
    url: att.url.startsWith("/api/") ? apiUrl(att.url) : att.url,
  };
}

export async function uploadIncidentAttachment(
  incidentId: string,
  file: File,
  note?: string,
): Promise<IncidentAttachment> {
  const form = new FormData();
  form.append("file", file);
  if (note) form.append("note", note);

  // Raw fetch (not `request`) so we don't force a JSON Content-Type — the
  // browser must set the multipart boundary itself.
  let res: Response;
  try {
    res = await apiFetch(apiUrl(`/api/incidents/${incidentId}/attachments/upload`), {
      method: "POST",
      credentials: "include",
      body: form,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return normalizeIncidentAttachmentUrl((data as { item: IncidentAttachment }).item);
}

export async function deleteIncidentAttachment(
  incidentId: string,
  attachmentId: string,
): Promise<void> {
  await request<void>(`/api/incidents/${incidentId}/attachments/${attachmentId}`, {
    method: "DELETE",
  });
}

// ── Linked / generated documents ───────────────────────────────────────────

export async function listIncidentDocuments(incidentId: string): Promise<IncidentDocument[]> {
  const data = await request<{ items: IncidentDocument[] }>(
    `/api/incidents/${incidentId}/documents`,
  );
  return data.items;
}
